/**
 * Builds the "maturity mix" corner-glow gradient for the Overview
 * Discovery/Delivery cards. The colors are the same L1–L5 level colors shown on
 * the phase pages' maturity-distribution bar (`--genai-sem-level-l*`), weighted
 * by how many items sit at each level — so a scope that is mostly L5 glows
 * green, while an even spread produces a multi-color band.
 *
 * Composition follows the Figma "Summary card" treatment (Modernization file,
 * node 418:8907): a near-horizontal solid color band with an opaque,
 * surface-colored radial laid ON TOP. Crucially the radial is opaque at its
 * CENTER (anchored low in the card so the whole lower area is clear surface) and
 * fades to a light veil at its OUTER edge, which falls across the top — so the
 * color reads as a pale pastel band confined to the top of the card, with a
 * *concave* lower boundary curving up into the surface (matching Figma, not a
 * convex corner blob). Because the knockout uses the surface token, dark mode
 * fades to the dark surface automatically. This module returns the full
 * `background` value; the card just paints it (`.phase-card::before`).
 */
import type { DiscoveryFeatureListView } from '@sdd-maturity/shared-types';
import { LEVEL_ORDER, normalizeLevel } from './score';

/** Near-horizontal sweep of the color band (matches the Figma ~96° angle). */
const BAND_ANGLE = '96deg';
/** Radial reveal shape/anchor — a wide ellipse centered low in the card, so its
 *  opaque core covers the lower area and only the top edge (farthest from the
 *  center) escapes the veil, keeping the color confined to the top. */
const REVEAL = 'ellipse 119% 95% at 33% 80%';
/** Radius (%) out to which the knockout is fully opaque surface — i.e. how much
 *  of the card stays clean surface before any color shows. */
const REVEAL_CLEAR_END = 62;
/** Surface veil (%) at the most-revealed outer edge. High on purpose: even at
 *  its strongest the band shows only ~(100−this)%, so it reads as a pale pastel
 *  wash (like the Figma) rather than a saturated block of color. */
const REVEAL_MIN_VEIL = 82;

/**
 * Turn a level histogram into the composed corner-glow `background` value.
 * Each present level gets one color stop at its cumulative-midpoint position
 * (widest band = dominant level), then a surface-colored radial knockout reveals
 * the band only as a pale band across the top. Returns `null` when there is
 * nothing to show, so callers render a plain card.
 */
export function buildMaturityGradient(levelCounts: Record<string, number>): string | null {
  const present = LEVEL_ORDER.filter((l) => (levelCounts[l] ?? 0) > 0);
  const total = present.reduce((sum, l) => sum + (levelCounts[l] ?? 0), 0);
  if (total === 0) return null;

  const stops: string[] = [];
  let cumulative = 0;
  present.forEach((level, i) => {
    const proportion = (levelCounts[level] ?? 0) / total;
    const midpoint = Math.round((cumulative + proportion / 2) * 100);
    const color = `var(--genai-sem-level-${level.toLowerCase()})`;
    if (i === 0) stops.push(`${color} 0%`);
    stops.push(`${color} ${midpoint}%`);
    if (i === present.length - 1) stops.push(`${color} 100%`);
    cumulative += proportion;
  });
  const band = `linear-gradient(${BAND_ANGLE}, ${stops.join(', ')})`;

  // Opaque surface from the center out to REVEAL_CLEAR_END (the clean lower
  // area), then fade to a light veil at the outer edge (the top). Mixing the
  // outer stop toward the surface token rather than the `transparent` keyword
  // both keeps the band pale and avoids muddy transparent-black edges.
  const surface = 'var(--genai-sem-surface-bg)';
  const knockout =
    `radial-gradient(${REVEAL}, ${surface} 0%, ${surface} ${REVEAL_CLEAR_END}%, ` +
    `color-mix(in srgb, ${surface} ${REVEAL_MIN_VEIL}%, transparent) 100%)`;

  // Knockout is listed first so it paints ON TOP of the band.
  return `${knockout}, ${band}`;
}

/**
 * Single-color variant of the maturity wash for one card whose level is known
 * (the dimension cards on the Feature/Repo pages). Same composition and veil as
 * buildMaturityGradient, but the band is one solid level color instead of the
 * weighted L1–L5 spectrum. Returns null for an unscored/unknown level so the
 * card renders plain.
 */
export function buildLevelGradient(level: string): string | null {
  const normalized = normalizeLevel(level);
  if (!normalized) return null;
  return buildMaturityGradient({ [normalized]: 1 });
}

/**
 * Tally repos by their model-assigned `overallLevel`. Pass already-active repos
 * (from `classifyRepos(...).active`) — mirrors RepoMaturityDistribution.
 */
export function countRepoLevels(repos: Array<{ overallLevel: string }>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const r of repos) {
    counts[r.overallLevel] = (counts[r.overallLevel] ?? 0) + 1;
  }
  return counts;
}

/**
 * Tally discovery features by maturity level. Mirrors the Discovery phase page:
 * reads `overallLevel` (falling back to `level`) and buckets anything
 * unrecognized as L1 so the mix matches the "Feature Distribution" bar.
 */
export function countFeatureLevels(features: DiscoveryFeatureListView[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const f of features) {
    const raw = f.overallLevel ?? f.level;
    const level = normalizeLevel(raw) || 'L1';
    counts[level] = (counts[level] ?? 0) + 1;
  }
  return counts;
}
